import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CheckCircleOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { FourM } from '../../../models';
import { UserContext } from '../../contexts';
import { FourMEditPage } from './/four-ms-edit-page';
import { EntityChangeLog } from '../../reports';

export function FourMsPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [fourMs, setFourMs] = useState<FourM[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalFourM, setEditModalFourM] = useState<FourM>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'fourMs.edit'));
  }, [user]);

  const loadFourMs = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`four-ms`)
      .then((response: any) => {
        setFourMs(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadFourMs();
  }, [loadFourMs]);

  const onRefreshClick = () => {
    loadFourMs();
  };

  const onFourMEditSaved = () => {
    closeEditModal();
    loadFourMs();
  };

  const openEditModal = () => {
    setEditModalFourM(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByFourM = (fourM: FourM) => {
    setEditModalFourM(fourM);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalFourM(undefined);
  };

  const deleteFourM = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`four-ms/${id}`)
      .then(() => {
        setLoading(false);
        loadFourMs();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmdeleteFourM = (id: string) => {
    const onOk = () => deleteFourM(id);
    Modal.confirm({
      title: '4M sil',
      icon: <ExclamationCircleOutlined />,
      content: '4M silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const fourM = fourMs?.find(m => m.id === id);
      if (fourM) {
        openEditModalByFourM(fourM);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmdeleteFourM(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, fourM: FourM) => (
        <Button type="link" onClick={() => openEditModalByFourM(fourM)}>
          {name}
        </Button>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, fourM: FourM) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(fourM.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <CheckCircleOutlined /> 4M'ler
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
                Yeni 4M
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={fourMs} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'FourM'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="4M" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <FourMEditPage fourM={editModalFourM} onSaved={onFourMEditSaved} onCancel={closeEditModal} />
      </Modal>
    </>
  );
}
